import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, ASSETS } from "@/data";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 backdrop-blur-xl transition-[background-color,border-color,box-shadow] duration-500 border-b ${
        scrolled ? "bg-white/90 border-black/10 shadow-sm" : "bg-white/70 border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          data-testid="header-bar"
          data-scrolled={scrolled}
          className={`flex items-center justify-between transition-[height] duration-500 ease-out ${scrolled ? "h-[72px]" : "h-[232px]"}`}
        >
          <button
            data-testid="logo-button"
            onClick={() => handleNav("hero")}
            className="flex items-center group shrink-0"
          >
            <img
              src={ASSETS.logo}
              alt="Φυσική Επιλογή — Φροντιστήρια Μ.Ε."
              data-testid="header-logo-img"
              className={`shrink-0 object-contain transition-[height,width,margin] duration-500 ease-out group-hover:scale-105 ${scrolled ? "h-[56px] w-[56px] mt-0" : "h-[208px] w-[208px] mt-4"}`}
            />
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="font-mono-accent text-xs uppercase tracking-widest px-4 py-2 text-foreground/70 hover:text-primary transition-colors"
              >
                {l.label}
              </button>
            ))}
            <a
              href="/calendar"
              data-testid="nav-link-calendar"
              className="font-mono-accent text-xs uppercase tracking-widest px-4 py-2 text-foreground/70 hover:text-primary transition-colors"
            >
              Ημερολόγιο
            </a>
            <a
              href="/vaseis"
              data-testid="nav-link-vaseis"
              className="font-mono-accent text-xs uppercase tracking-widest px-4 py-2 text-foreground/70 hover:text-primary transition-colors"
            >
              Βάσεις 2026
            </a>
            <a
              href="/test"
              data-testid="nav-link-test"
              className="font-mono-accent text-xs uppercase tracking-widest px-4 py-2 text-foreground/70 hover:text-primary transition-colors"
            >
              Δωρεάν Τεστ
            </a>
            <a href="/members" data-testid="nav-link-members" className="font-mono-accent text-xs uppercase tracking-widest px-4 py-2 text-primary font-bold hover:text-accent transition-colors whitespace-nowrap">
              Μέλη
            </a>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setOpen(!open)}
            aria-label="menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-black/10 bg-white">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="font-mono-accent text-xs uppercase tracking-widest py-3 text-left border-b border-border/60"
              >
                {l.label}
              </button>
            ))}
            <a
              href="/calendar"
              data-testid="mobile-nav-link-calendar"
              className="font-mono-accent text-xs uppercase tracking-widest py-3 text-left border-b border-border/60"
            >
              Ημερολόγιο
            </a>
            <a
              href="/vaseis"
              data-testid="mobile-nav-link-vaseis"
              className="font-mono-accent text-xs uppercase tracking-widest py-3 text-left border-b border-border/60"
            >
              Βάσεις 2026
            </a>
            <a
              href="/test"
              data-testid="mobile-nav-link-test"
              className="font-mono-accent text-xs uppercase tracking-widest py-3 text-left border-b border-border/60"
            >
              Δωρεάν Τεστ
            </a>
            <a href="/members" data-testid="mobile-nav-link-members" className="font-mono-accent text-xs uppercase tracking-widest py-3 text-left border-b border-border/60 text-primary font-bold">
              Περιοχή Μελών
            </a>
            <button
              data-testid="mobile-cta-enroll"
              onClick={() => handleNav("enroll")}
              className="mt-3 font-mono-accent text-xs uppercase tracking-widest bg-primary text-primary-foreground px-5 py-3 rounded-sm"
            >
              Εγγραφές 2026
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
